'use client';

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import React from 'react';
import Link from 'next/link';

import { useInView } from '@/slw/shared/lib/animation';
import { Button } from '@/slw/shared/ui/button';

interface ImageTextSectionProps {
  heading: string;
  headingColor?: string;
  text: string;
  image: string;
  imageAlt: string;
  imageLeft?: boolean;
  ctaLabel?: string;
  ctaHref?: string;
  onCtaClick?: () => void;
  children?: React.ReactNode;
}

export function ImageTextSection({
  heading,
  text,
  image,
  imageAlt,
  imageLeft = false,
  ctaLabel,
  ctaHref,
  onCtaClick,
  children,
}: ImageTextSectionProps) {
  const [ref, inView] = useInView(0.18);

  return (
    <section ref={ref} className='w-full  md:[&:not(:last-child)]:pb-16 [&:not(:last-child)]:pb-8'>
      <motion.div
        className={`flex flex-col md:flex-row items-center gap-6 md:gap-15 ${imageLeft ? 'md:flex-row-reverse' : ''}`}
        initial={{ opacity: 0, y: 40 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      >
        <motion.div
          className='w-full md:w-1/2 group overflow-hidden rounded-xl shadow-lg hover:[&_img]:transform-[scale(1.2)]'
          whileHover={{ scale: 1.045 }}
          transition={{ type: 'spring', stiffness: 200, damping: 18 }}
        >
          <img
            src={image}
            alt={imageAlt}
            className='w-full h-64 md:h-80 object-cover object-center 
            transition-transform duration-300'
            draggable={false}
          />
        </motion.div>
        <div className='w-full md:w-1/2 flex flex-col md:gap-12 gap-6'>
          <h2 className='text-xl md:text-[2rem] font-bold'>{heading}</h2>
          <p className='text-sm'>{text}</p>
          {children}
          {ctaLabel && onCtaClick && !ctaHref ? (
            <Button
              variant='ghost'
              color='primary'
              className='w-fit p-0 h-6 gap-2 font-bold text-base hover:underline'
              size='lg'
              onClick={onCtaClick}
            >
              <ArrowRight size={16} />
              {ctaLabel}
            </Button>
          ) : (
            <Link href={ctaHref || ''} className='flex text-primary items-center gap-2 font-bold hover:underline'>
              <ArrowRight size={16} />
              {ctaLabel}
            </Link>
          )}
        </div>
      </motion.div>
    </section>
  );
}
